/* assets/meters-quantity.js */
(() => {
  if (window.MetersQty) return;

  const API = {
    initAll: () => initWithin(document),
    initWithin,
  };
  window.MetersQty = API;

  // Guard anti-race persistente tra i re-render del carrello: tiene traccia delle
  // righe con un update /cart/change già in volo, evitando due fetch concorrenti
  // sulla stessa riga (causa storica della property Metri disallineata dalla
  // quantità addebitata). Vive a livello di modulo così sopravvive ai re-render,
  // che ricreano il closure di initWrap azzerandone i flag locali.
  const cartLineInFlight = new Set();

  // Avvisi "disponibilità aggiornata" da mostrare sulla riga dopo il suo re-render
  // (il re-render del carrello sostituisce l'HTML e cancellerebbe il messaggio).
  const stockNotices = new Map();

  // Token univoco per riga d'ordine: impedisce a Shopify di fondere due aggiunte
  // dello stesso tessuto+metri in un'unica riga (la fusione somma la quantity ma
  // lascia invariata la property Metri → metri pagati ≠ metri indicati sull'ordine).
  function uniqueCutToken() {
    return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 8);
  }

  // ---------------------------------------------------------------------------
  // Riallineamento Metri ↔ quantità reale in carrello.
  // Se la quantità richiesta supera la scorta (inventory policy "deny"), Shopify NON
  // rifiuta /cart/add né /cart/change: abbassa la quantità al massimo disponibile
  // (risposta 422 "Puoi aggiungere al massimo N unità") ma CONSERVA la property Metri
  // inviata → metri sull'ordine > metri pagati (es. TOS41477: 3,00 m chiesti, 2,70 pagati).
  // Le funzioni qui sotto riportano Metri alla quantità effettivamente in carrello.
  // ---------------------------------------------------------------------------
  const METRI_FACTOR = 10; // in carrello l'unità Shopify è sempre il decimetro

  // Shopify/Liquid può serializzare item.properties come oggetto, come array di coppie
  // [key, value] o come oggetti { first, last }: normalizza sempre a oggetto.
  function parsePropsJSON(raw) {
    let props = {};
    if (!raw) return props;
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (Array.isArray(parsed)) {
        parsed.forEach((entry) => {
          if (Array.isArray(entry) && entry.length >= 2) {
            const key = String(entry[0] ?? "").trim();
            if (key) props[key] = entry[1];
            return;
          }
          if (entry && typeof entry === "object") {
            const key = String(entry.first ?? entry.key ?? "").trim();
            const value = entry.last ?? entry.value ?? "";
            if (key) props[key] = value;
          }
        });
      } else if (parsed && typeof parsed === "object") {
        props = { ...parsed };
      }
    } catch (e) {
      props = {};
    }
    return props;
  }

  function metriKeyOf(props) {
    return Object.keys(props || {}).find((k) => /metri/i.test(k) && !k.startsWith("_")) || null;
  }

  function metriMatchesQty(value, quantity) {
    const m = parseFloat(String(value ?? "").replace(",", "."));
    return Number.isFinite(m) && Math.round(m * METRI_FACTOR) === Number(quantity);
  }

  function metriFromQty(quantity) {
    return (Number(quantity) / METRI_FACTOR).toFixed(2) + " m";
  }

  function cartRouteJs(key, fallback) {
    const base = (window.routes && window.routes[key]) || fallback;
    return String(base).replace(/\.js$/, "") + ".js";
  }

  // Legge il carrello reale e corregge (via /cart/change, senza toccare le quantità)
  // ogni riga venduta a metri la cui property Metri non corrisponde alla quantità.
  // Restituisce quante righe ha corretto. Chiamate concorrenti condividono la stessa esecuzione.
  let reconcileInFlight = null;
  function reconcileCartMetri() {
    if (reconcileInFlight) return reconcileInFlight;
    const headers = { "Content-Type": "application/json", Accept: "application/json" };
    reconcileInFlight = (async () => {
      const cart = await fetch(cartRouteJs("cart_url", "/cart"), { headers }).then((r) => r.json());
      let fixed = 0;
      const items = cart.items || [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const props = item.properties || {};
        const key = metriKeyOf(props);
        if (!key || metriMatchesQty(props[key], item.quantity)) continue;
        await fetch(cartRouteJs("cart_change_url", "/cart/change"), {
          method: "POST",
          headers,
          // chiave univoca della riga (non l'indice): resta valida anche se Shopify
          // riordina le righe dopo una correzione precedente
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity,
            properties: { ...props, [key]: metriFromQty(item.quantity) },
          }),
        });
        fixed += 1;
      }
      return fixed;
    })()
      .catch((e) => {
        console.error("[MetersQty] riallineamento Metri non riuscito", e);
        return 0;
      })
      .finally(() => {
        reconcileInFlight = null;
      });
    return reconcileInFlight;
  }

  function findCartEl(from) {
    return (
      (from && from.closest && (from.closest("cart-items") || from.closest("cart-drawer-items"))) ||
      document.querySelector("cart-drawer-items") ||
      document.querySelector("cart-items")
    );
  }

  function normalize(s) {
    return String(s || "").trim().replace(",", ".");
  }

  function isValidMax2Decimals(raw) {
    return /^\d+([.,]\d{0,2})?$/.test(String(raw || "").trim());
  }

  // Sanitizza duramente: solo цифre + 1 separatore + max 2 decimali
  function sanitizeToMax2Decimals(raw) {
    raw = String(raw || "").replace(/\s+/g, "");
    raw = raw.replace(/[^\d.,]/g, "");

    const firstSepIdx = raw.search(/[.,]/);
    if (firstSepIdx !== -1) {
      const sep = raw[firstSepIdx];
      const before = raw.slice(0, firstSepIdx).replace(/[^\d]/g, "");
      const after = raw.slice(firstSepIdx + 1).replace(/[^\d]/g, "");
      return after.length ? `${before}${sep}${after.slice(0, 2)}` : `${before}${sep}`;
    }

    return raw.replace(/[^\d]/g, "");
  }

  // Converte metri stringa -> unità intera (cm se factor=100, dm se factor=10) senza float
  function metersStringToUnitInt(raw, factor) {
    const t = String(raw || "").trim();
    if (!t) return null;

    if (!isValidMax2Decimals(t)) return "INVALID";

    const norm = normalize(t);
    const [intPartRaw, decPartRaw = ""] = norm.split(".");
    const intPart = parseInt(intPartRaw, 10);
    if (!Number.isFinite(intPart)) return null;

    const dec2 = (decPartRaw + "00").slice(0, 2);
    const d1 = parseInt(dec2[0] || "0", 10);
    const d2 = parseInt(dec2[1] || "0", 10);

    // modalità "cm" (vecchia)
    if (factor === 100) {
      const decPart = parseInt(dec2, 10);
      return intPart * 100 + decPart;
    }

    // modalità "dm" (nuova): accetta solo multipli di 0,10 m
    if (factor === 10) {
      // se ci sono 2 decimali, il secondo deve essere 0 (es. 1,20 ok; 1,23 no)
      if (decPartRaw.length >= 2 && d2 !== 0) return "INVALID_STEP";
      // "1,2" lo consideriamo 1,20
      return intPart * 10 + d1;
    }

    return null;
  }

  function cmToMetersDisplay(cm, factor) {
    const m = cm / factor;
    return m.toFixed(2).replace(".", ","); // sempre 2 decimali
  }

  function initWithin(root) {
    root.querySelectorAll(".js-meters-qty").forEach(initWrap);
    root.querySelectorAll(".js-quantity-max").forEach(initPiecesWrap);
  }

  // Stesso comportamento righe 166-171: se superi la quantità massima → showError + blocca submit
  function initPiecesWrap(wrap) {
    if (!wrap || wrap.dataset.quantityMaxInit === "1") return;
    const input = wrap.querySelector('input[name="quantity"]');
    const errorEl = wrap.querySelector(".js-meters-qty__error");
    if (!input) return;
    const maxRaw = input.dataset.max || input.getAttribute("max");
    if (maxRaw == null || maxRaw === "") return;
    const maxNum = parseInt(maxRaw, 10);
    if (!Number.isFinite(maxNum)) return;
    wrap.dataset.quantityMaxInit = "1";

    const showError = (msg = "", showInDom = false) => {
      input.setCustomValidity(msg || "");
      if (errorEl) {
        if (showInDom) {
          errorEl.textContent = msg || "";
          errorEl.hidden = !msg;
        } else {
          errorEl.textContent = "";
          errorEl.hidden = true;
        }
      }
    };

    const validate = (showInDom = false) => {
      const val = parseInt(input.value, 10);
      if (!Number.isFinite(val) || val <= maxNum) {
        showError("", true);
        return true;
      }
      showError(`Disponibilità massima: ${maxNum}`, showInDom);
      return false;
    };

    const formId = input.getAttribute("form");
    const formEl = formId ? document.getElementById(formId) : input.form;
    if (formEl && !formEl.__quantityMaxSubmitBound) {
      formEl.__quantityMaxSubmitBound = true;
      formEl.addEventListener(
        "submit",
        (e) => {
          const wraps = formEl.querySelectorAll(".js-quantity-max");
          for (const w of wraps) {
            const inp = w.querySelector('input[name="quantity"]');
            if (!inp) continue;
            const maxR = inp.dataset.max || inp.getAttribute("max");
            if (maxR == null || maxR === "") continue;
            const mx = parseInt(maxR, 10);
            const v = parseInt(inp.value, 10);
            if (Number.isFinite(v) && v > mx) {
              e.preventDefault();
              e.stopPropagation();
              inp.setCustomValidity("Disponibilità massima: " + mx);
              const err = w.querySelector(".js-meters-qty__error");
              if (err) {
                err.textContent = "Disponibilità massima: " + mx;
                err.hidden = false;
              }
              inp.reportValidity();
              return;
            }
            inp.setCustomValidity("");
          }
        },
        true
      );
    }

    input.addEventListener("input", () => validate(false));
    input.addEventListener("change", () => validate(false));
    input.addEventListener("blur", () => validate(false));
  }

  function initWrap(wrap) {
    if (!wrap || wrap.dataset.metersQtyInit === "1") return;
    wrap.dataset.metersQtyInit = "1";

    // ✅ fallback a 10 (dm) per evitare ritorni “silenziosi” a 100
    const factor = parseInt(wrap.dataset.factor || "10", 10);

    const metersInput = wrap.querySelector(".js-meters-qty__meters");
    const cmInput = wrap.querySelector(".js-meters-qty__cm");
    const errorEl = wrap.querySelector(".js-meters-qty__error");
    const propInput = wrap.querySelector(".js-meters-qty__prop");
    const cutInput = wrap.querySelector(".js-meters-qty__cut");

    if (!metersInput || !cmInput) return;

    const isCartUpdates = cmInput.getAttribute("name") === "updates[]";

    // Seed del token univoco anti-fusione per i form prodotto (fallback nel caso
    // il submit non lo rigeneri). Le righe già nel carrello non ne hanno bisogno.
    if (cutInput && !isCartUpdates && !cutInput.value) cutInput.value = uniqueCutToken();

    if (!isCartUpdates) {
      const formId = cmInput.getAttribute("form");
      const formEl =
        formId && document.getElementById(formId) ? document.getElementById(formId) : cmInput.form;

      if (formEl && !formEl.__metersQtySubmitBound) {
        formEl.__metersQtySubmitBound = true;

        formEl.addEventListener(
          "submit",
          (e) => {
            const wraps = formEl.querySelectorAll(".js-meters-qty");
            for (const w of wraps) {
              const mi = w.querySelector(".js-meters-qty__meters");
              const ci = w.querySelector(".js-meters-qty__cm");
              if (!mi || !ci) continue;

              mi.dispatchEvent(new Event("change", { bubbles: true }));

              if (!mi.checkValidity()) {
                e.preventDefault();
                e.stopPropagation();
                mi.reportValidity();
                break;
              }

              // Riga valida: rigenera il token univoco così ogni "aggiungi al
              // carrello" resta una riga separata e non viene fusa con una
              // precedente (fusione = quantity sommata ma property Metri invariata).
              const cut = w.querySelector(".js-meters-qty__cut");
              if (cut) cut.value = uniqueCutToken();
            }
          },
          true
        );
      }
    }

    const showError = (msg = "") => {
      if (errorEl) {
        if (isCartUpdates && msg) {
          errorEl.textContent = msg;
          errorEl.hidden = false;
        } else {
          errorEl.textContent = "";
          errorEl.hidden = true;
        }
      }
      metersInput.setCustomValidity(msg || "");
    };

    const getRules = () => {
      const minRaw = cmInput.dataset.min ?? cmInput.getAttribute("min");
      const maxRaw = cmInput.dataset.max ?? cmInput.getAttribute("max");

              const minCm = Math.max(Number.isFinite(parseInt(minRaw, 10)) ? parseInt(minRaw, 10) : 0, 5); // minimo 50cm = 5dm
      const maxCm =
        maxRaw !== null && maxRaw !== "" && maxRaw !== "null" && Number.isFinite(parseInt(maxRaw, 10))
          ? parseInt(maxRaw, 10)
          : null;

      return { minCm, maxCm };
    };

    const clampCm = (cm) => {
      const { minCm, maxCm } = getRules();
      cm = Math.max(minCm, cm);
      if (maxCm !== null && !Number.isNaN(maxCm)) cm = Math.min(maxCm, cm);
      return cm;
    };

    const triggerCartUpdate = () => {
      if (!isCartUpdates) return;
      cmInput.dispatchEvent(new Event("input", { bubbles: true }));
      cmInput.dispatchEvent(new Event("change", { bubbles: true }));
    };

    const getUpdatedPropertiesForCart = (dmValue) => {
      if (!isCartUpdates) return null;
      // Metri derivati dalla quantità che stiamo per inviare (dmValue = cmRaw calcolato
      // da applyFromMeters), non dal testo digitato né da cmInput (che in cart mode non
      // viene più aggiornato): così la property inviata a /cart/change coincide sempre
      // con la quantità addebitata.
      const dm = Number.isFinite(dmValue) ? dmValue : parseInt(cmInput.value, 10);
      const currentMeters = Number.isFinite(dm)
        ? (dm / factor).toFixed(2) + " m"
        : metersInput.value.trim().replace(",", ".") + " m";
      const props = parsePropsJSON(wrap.dataset.properties);

      // Pulisce eventuali chiavi legacy numeriche generate da update precedenti.
      Object.keys(props).forEach((key) => {
        if (/^\d+$/.test(key)) delete props[key];
      });

      let hasMetriKey = false;
      Object.keys(props).forEach((key) => {
        if (/metri/i.test(key)) {
          props[key] = currentMeters;
          hasMetriKey = true;
        }
      });

      if (!hasMetriKey) props.Metri = currentMeters;
      return props;
    };

    const applyFromMeters = (forceRewrite, triggerUpdate) => {
      const raw = sanitizeToMax2Decimals(metersInput.value);
      if (raw !== metersInput.value) metersInput.value = raw;

      const trimmed = String(raw || "").trim();

      // ✅ stato intermedio: "1," / "1." -> deve BLOCCARE
      if (/^\d+[.,]$/.test(trimmed)) {
        showError("Completa il valore (es. 1,20)");
        return false;
      }

      // ✅ vuoto non valido in submit/change
      if (!trimmed) {
        showError("Inserisci un valore valido (es. 1,20)");
        return false;
      }

      if (!isValidMax2Decimals(trimmed)) {
        showError("Inserisci un valore valido (es. 1,20)");
        return false;
      }

      const cmRaw = metersStringToUnitInt(trimmed, factor);

      if (cmRaw === "INVALID_STEP") {
        showError("Inserisci multipli di 0,10 m (es. 1,20)");
        return false;
      }

      if (cmRaw === "INVALID" || cmRaw === null) {
        showError("Inserisci un valore valido (es. 1,20)");
        return false;
      }

      const { minCm, maxCm } = getRules();

      if (cmRaw < minCm) {
        showError(`Minimo: ${cmToMetersDisplay(minCm, factor)} m`);
        return false;
      }

      if (maxCm !== null && cmRaw > maxCm) {
        showError(`Disponibilità massima: ${cmToMetersDisplay(maxCm, factor)} m`);
        return false;
      }

      showError("");
      // In cart mode NON scrivere updates[] (cmInput): la quantità viene inviata solo
      // in modo atomico da Applica/Enter (updateQuantity, insieme alla property Metri).
      // Così un submit del form carrello (es. click su Checkout, che fa perdere il
      // focus al campo → blur → applyFromMeters) non può committare una quantità
      // digitata ma non applicata lasciando indietro la property Metri. Il valore
      // calcolato (cmRaw) torna a chi chiama, che lo passa esplicitamente a updateQuantity.
      if (!isCartUpdates) cmInput.value = String(cmRaw);

      if (forceRewrite) metersInput.value = cmToMetersDisplay(cmRaw, factor);
      // La property Metri è SEMPRE derivata dalla quantità reale (cmRaw = ciò che
      // viene addebitato), non dal testo digitato, così non può divergere.
      if (propInput) propInput.value = cmToMetersDisplay(cmRaw, factor).replace(",", ".") + " m";
      if (triggerUpdate) triggerCartUpdate();

      return cmRaw;
    };

    // INIT: usa il valore reale (unità) e mostra metri
    (function init() {
      // ✅ fallback coerente: 1 metro = factor unità (10dm o 100cm)
      const rawCm = parseInt(cmInput.value || String(factor), 10);
      // In carrello cmInput (updates[]) è la quantità REALE della riga: non va limitata né
      // riscritta, altrimenti un submit del form (checkout) cambierebbe la quantità senza
      // aggiornare la property Metri. Il clamp serve solo sulla pagina prodotto.
      const cm = isCartUpdates && Number.isFinite(rawCm) ? rawCm : clampCm(rawCm);
      if (!isCartUpdates) cmInput.value = String(cm);

      if (propInput) propInput.value = cmToMetersDisplay(cm, factor).trim().replace(",", ".") + " m";

      if (cm % factor === 0) {
        metersInput.value = String(cm / factor).replace(".", ",");
      } else {
        metersInput.value = cmToMetersDisplay(cm, factor);
      }
    })();

    // Controllo al disegno della riga di carrello:
    // 1) Metri disallineata (es. aggiunta dalla pagina prodotto oltre scorta: Shopify ha messo
    //    la quantità massima ma tenuto la Metri richiesta) → riallinea Metri alla quantità reale;
    // 2) quantità oltre la scorta attuale (es. qualcun altro ha comprato nel frattempo) → porta
    //    la riga al massimo disponibile INSIEME a Metri, invece di lasciare che sia il checkout
    //    a ridurla da solo lasciando la Metri vecchia.
    if (isCartUpdates) {
      const lineKey = cmInput.dataset.index != null ? String(cmInput.dataset.index) : null;

      // avviso lasciato dal riallineamento precedente (la riga è stata appena ridisegnata)
      if (lineKey && stockNotices.has(lineKey) && errorEl) {
        errorEl.textContent = stockNotices.get(lineKey);
        errorEl.hidden = false;
        stockNotices.delete(lineKey);
      }

      const renderedProps = parsePropsJSON(wrap.dataset.properties);
      const metriKey = metriKeyOf(renderedProps);
      const qty = parseInt(cmInput.value, 10);
      const { maxCm } = getRules();
      // scorta 0: la riga non si tocca (il checkout la blocca comunque, nessun rischio di
      // metri sbagliati); si riduce solo in caso di disponibilità parziale
      const targetQty = maxCm !== null && maxCm > 0 && Number.isFinite(qty) && qty > maxCm ? maxCm : qty;

      if (
        metriKey &&
        lineKey &&
        Number.isFinite(qty) &&
        qty > 0 &&
        (targetQty !== qty || !metriMatchesQty(renderedProps[metriKey], qty)) &&
        !cartLineInFlight.has(lineKey)
      ) {
        const cartEl = findCartEl(wrap);
        if (cartEl && typeof cartEl.updateQuantity === "function") {
          Object.keys(renderedProps).forEach((k) => {
            if (/^\d+$/.test(k)) delete renderedProps[k];
          });
          if (targetQty !== qty) {
            stockNotices.set(lineKey, `Disponibilità aggiornata: massimo ${cmToMetersDisplay(targetQty, factor)} m`);
          }
          cartLineInFlight.add(lineKey);
          Promise.resolve(
            cartEl.updateQuantity(lineKey, String(targetQty), metersInput.getAttribute("name"), {
              ...renderedProps,
              [metriKey]: metriFromQty(targetQty),
            })
          ).finally(() => cartLineInFlight.delete(lineKey));
        }
      }
    }

    metersInput.addEventListener("keydown", (e) => {
      const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Tab", "Enter", "Home", "End"];
      if (allowed.includes(e.key)) return;

      if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;

      if (e.key.length === 1 && !/[0-9.,]/.test(e.key)) e.preventDefault();

      if ((e.key === "," || e.key === ".") && /[.,]/.test(metersInput.value)) {
        const sel = metersInput.selectionStart !== metersInput.selectionEnd;
        if (!sel) e.preventDefault();
      }
    });

    const isAllowedValue = (val) => /^\d*([.,]\d{0,2})?$/.test(val);

    metersInput.addEventListener("beforeinput", (e) => {
      if (e.inputType && e.inputType.startsWith("delete")) return;
      if (typeof e.data !== "string") return;

      const el = metersInput;
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;

      const next = el.value.slice(0, start) + e.data + el.value.slice(end);

      if (!isAllowedValue(next)) {
        e.preventDefault();
        showError();
      }
    });

    metersInput.addEventListener("paste", (e) => {
      e.preventDefault();

      const pasted = (e.clipboardData || window.clipboardData).getData("text") || "";
      const el = metersInput;
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;

      const next = el.value.slice(0, start) + pasted + el.value.slice(end);
      metersInput.value = sanitizeToMax2Decimals(next);

      showError("");
      applyFromMeters(false, false);
    });

    metersInput.addEventListener("input", () => {
      const raw = metersInput.value;
      const sanitized = sanitizeToMax2Decimals(raw);
      if (sanitized !== raw) metersInput.value = sanitized;

      const trimmed = String(metersInput.value || "").trim();

      if (trimmed !== "" && !isValidMax2Decimals(trimmed) && !/^\d+[.,]$/.test(trimmed)) {
        showError("Inserisci un valore valido (es. 1,20)");
        return;
      }

      const cmRaw = metersStringToUnitInt(trimmed, factor);

      if (cmRaw === "INVALID_STEP") {
        showError("Inserisci multipli di 0,10 m (es. 1,20)");
        return;
      }

      if (typeof cmRaw === "number") {
        const { minCm, maxCm } = getRules();

        if (cmRaw < minCm) {
          showError(`Minimo: ${cmToMetersDisplay(minCm, factor)} m`);
          return;
        }

        if (maxCm !== null && cmRaw > maxCm) {
          showError(`Disponibilità massima: ${cmToMetersDisplay(maxCm, factor)} m`);
          return;
        }

        showError("");
        // Nel carrello NON aggiornare updates[] a ogni tasto digitato: la quantità
        // deve cambiare solo in modo atomico via Applica/Enter (updateQuantity, che
        // invia quantità e property Metri insieme). Aggiornare updates[] "live"
        // permetteva di arrivare al checkout con la quantità nuova ma la property
        // Metri vecchia → metri sull'ordine ≠ metri pagati (righe singole tipo qty 22/30).
        if (!isCartUpdates) cmInput.value = String(cmRaw);
      }
    });

    metersInput.addEventListener("change", (e) => {
      e.stopPropagation();
      const ok = applyFromMeters(true, false);
      if (!ok && !isCartUpdates) metersInput.reportValidity();
    });

    metersInput.addEventListener("blur", () => {
      const raw = String(metersInput.value || "").trim();
      if (!raw) return;
      applyFromMeters(true, false);
    });

    // Un solo aggiornamento carrello alla volta per questa riga: blocca Applica/Enter
    // finché la richiesta precedente non è tornata, per evitare due fetch concorrenti
    // sulla stessa riga (causa della property Metri disallineata dalla quantità
    // addebitata). Il flag vive a livello di modulo (cartLineInFlight), keyed per
    // indice riga, così sopravvive ai re-render del carrello che ricreano questo closure.
    const applyBtn = wrap.querySelector(".js-meters-qty__apply");

    const triggerCartUpdateFromInput = (focusAfter) => {
      const lineKey = cmInput.dataset.index != null ? String(cmInput.dataset.index) : null;
      if (lineKey && cartLineInFlight.has(lineKey)) return;

      // dm = quantità reale calcolata (cmRaw), unica fonte di verità per l'update:
      // in cart mode cmInput.value non viene più toccato da digitazione/blur, quindi
      // passiamo dm esplicitamente a updateQuantity e deriviamo Metri dallo stesso dm.
      const dm = applyFromMeters(true, false);
      if (!dm) {
        if (!isCartUpdates) metersInput.reportValidity();
        return;
      }
      // closest() può fallire se wrap è detached dopo un re-render — fallback a document.querySelector
      const cartEl =
        wrap.closest("cart-items") ||
        wrap.closest("cart-drawer-items") ||
        document.querySelector("cart-items") ||
        document.querySelector("cart-drawer-items");
      if (cartEl && typeof cartEl.updateQuantity === "function") {
        if (lineKey) cartLineInFlight.add(lineKey);
        if (applyBtn) applyBtn.disabled = true;

        const lineIndex = cmInput.dataset.index;
        const inputName = metersInput.getAttribute("name");

        Promise.resolve(cartEl.updateQuantity(lineIndex, String(dm), inputName, getUpdatedPropertiesForCart(dm)))
          // Se Shopify ha ridotto la quantità (scorta insufficiente → 422), la Metri appena
          // inviata non corrisponde più alla quantità reale: la riallineiamo e ridisegniamo
          // il carrello (cart.js non ridisegna sulle risposte 422, il cliente vedrebbe ancora
          // il valore richiesto).
          .then(() => fetch(cartRouteJs("cart_url", "/cart"), { headers: { Accept: "application/json" } }))
          .then((r) => r.json())
          .then((cart) => {
            const item = (cart.items || [])[Number(lineIndex) - 1];
            if (!item) return;
            const props = item.properties || {};
            const key = metriKeyOf(props) || "Metri";
            if (metriMatchesQty(props[key], item.quantity)) return;
            // dopo il re-render l'elemento originale può essere staccato dal DOM
            const liveCartEl = document.querySelector(cartEl.tagName.toLowerCase()) || cartEl;
            return liveCartEl.updateQuantity(lineIndex, String(item.quantity), inputName, {
              ...props,
              [key]: metriFromQty(item.quantity),
            });
          })
          .catch((e) => console.error("[MetersQty] verifica quantità dopo l'update non riuscita", e))
          .finally(() => {
            if (lineKey) cartLineInFlight.delete(lineKey);
            if (applyBtn) applyBtn.disabled = false;
          });
      }
      if (focusAfter) metersInput.focus();
    };

    metersInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        triggerCartUpdateFromInput(false);
      }
    });

    if (applyBtn) {
      applyBtn.addEventListener("click", () => {
        triggerCartUpdateFromInput(true);
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => API.initAll());
  } else {
    API.initAll();
  }

  // Aggiunta dalla pagina prodotto oltre scorta: Shopify aggiunge comunque la quantità
  // massima con la Metri richiesta e product-form.js pubblica cartError → riallinea.
  // Registrato a DOM pronto così pubsub.js/constants.js sono sicuramente caricati.
  const bindCartErrorReconcile = () => {
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") return;
    if (!PUB_SUB_EVENTS.cartError) return;
    subscribe(PUB_SUB_EVENTS.cartError, () => {
      reconcileCartMetri();
    });
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bindCartErrorReconcile);
  } else {
    bindCartErrorReconcile();
  }

  // Ultima rete prima del checkout: riallinea tutte le righe a metri sul carrello reale,
  // poi prosegue. Il re-submit esclude gli updates[] del form: in questo tema ogni
  // modifica di quantità è già salvata via AJAX (✓ per i metri, onChange per le altre),
  // e gli updates[] di un carrello non ridisegnato (es. dopo un 422) sono posizionali e
  // potrebbero applicare quantità vecchie alle righe sbagliate senza toccare Metri.
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      const submitter = e.submitter;
      if (!form || !submitter || submitter.name !== "checkout") return;
      if (form.__metersCheckoutReady) {
        form.__metersCheckoutReady = false;
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();
      submitter.disabled = true;

      reconcileCartMetri().finally(() => {
        submitter.disabled = false;
        const updates = Array.from(form.elements || []).filter((el) => el.name === "updates[]" && !el.disabled);
        updates.forEach((el) => (el.disabled = true));
        form.__metersCheckoutReady = true;
        if (typeof form.requestSubmit === "function") form.requestSubmit(submitter);
        else submitter.click();
        // i dati del submit sono già stati raccolti: ripristina gli input per la pagina
        updates.forEach((el) => (el.disabled = false));
      });
    },
    true
  );

  const staticTargets = [
    document.getElementById("CartDrawer"),
    document.getElementById("main-cart-items"),
  ];

  const productInfoTargets = Array.from(document.querySelectorAll(".product__info-container"));
  const targets = [...staticTargets, ...productInfoTargets].filter(Boolean);

  const observers = [];
  targets.forEach((t) => {
    const obs = new MutationObserver(() => {
      API.initWithin(t);
    });
    obs.observe(t, { childList: true, subtree: true });
    observers.push(obs);
  });
})();
